import { Campaign, CampaignRecipient, CampaignSimpleStats, Template, TemplateType } from '@dev/ee-api/ee-api-models';
import { ApplicationState } from '@/store';

import { usePilotPlanConfig } from '@/services/hooks/usePilotPlanConfig';
import {
    CampaignCreatorActionTypes,
    SetDefaultCampaignAction,
    LoadCampaignStartAction,
    LoadCampaignSuccessAction,
    LoadCampaignErrorAction,
    SaveCampaignStartAction,
    SaveCampaignSuccessAction,
    SaveCampaignErrorAction,
    ClearPickedCampaignAction,
    VerifyCampaignAction,
    CountRecipientsStartAction,
    CountRecipientsSuccessAction,
    CountRecipientsErrorAction,
    SetCampaignErrorsAction,
    SetCampaignErrorsSubscriberAction,
    CampaignCreatorState,
    CampaignErrors,
    CampaignErrorSubscriber,
    SelectTemplateAction,
    DeselectTemplateAction,
    OpenGalleryAction,
    CloseGalleryAction,
    OpenEditorDragAndDropAction,
    CloseEditorDragAndDropAction,
    LoadTemplateStartAction,
    LoadTemplateSuccessAction,
    LoadTemplateErrorAction,
    AddCampaignAttachmentAction,
    SetActiveTemplateIndexAction,
    RemoveCampaignAttachmentAction,
    SetFirstErrorFocusedAction,
    SetDefaultTargetAction,
    LoadCompletedCampaignsCountErrorAction,
    LoadCompletedCampaignsCountSuccessAction,
    LoadCompletedCampaignsCountStartAction,
    CheckEnteredDomainStartAction,
    CheckEnteredDomainSuccessAction,
    CheckEnteredDomainErrorAction,
    SendTestAction,
    ModifyCampaignSendTestAction,
    ContinueWithHTMLTemplateAction,
    EditCampaignTemplateAction,
    SetAbTabsAction,
    AbTabsType,
    SetAbTabsErrorAction,
    RemoveAbTabsAction,
    SaveInMyTemplatesAction,
    SaveInMyTemplatesType,
    OpenPreviewOverlayAction,
    ClosePreviewOverlayAction,
    SwitchUTMAction,
    SetSelectedTemplatesAction,
    SelectedTemplateType,
    AddNewCampaignTemplateAction,
    SetActiveUtmTabIndexAction,
    ModifyCampaignMapAction,
    LoadCampaignSimpleStatsStartAction,
    LoadCampaignSimpleStatsSuccessAction,
    LoadCampaignSimpleStatsErrorAction,
    SetAbRecipientsAction,
    AbRecipientsType,
    SetRecalcAbRecipientsAction,
    StartRemoveAbTabsAction,
    LoadAttachmentStartAction,
    LoadAttachmentFinishAction,
    OpenTemplateEditorAction,
    CloseTemplateEditorAction,
    SaveCampaignParams,
} from './types';

export const SetDefaultCampaign = (
    isTestingCompleted: boolean,
    isAccountTrusted: boolean,
    defaultSenderEmail: string,
    defaultSenderName: string,
    isAbCampaign?: boolean,
    pilotPlanCampaign?: ReturnType<typeof usePilotPlanConfig>['pilotDefaultCampaignOverride']
): SetDefaultCampaignAction => ({
    type: CampaignCreatorActionTypes.SET_DEFAULT_CAMPAIGN,
    payload: {
        isTestingCompleted,
        isAccountTrusted,
        defaultSenderEmail,
        defaultSenderName,
        isAbCampaign,
        pilotPlanCampaign,
    },
});

export const SetDefaultTarget = (targets: CampaignRecipient): SetDefaultTargetAction => ({
    type: CampaignCreatorActionTypes.SET_DEFAULT_TARGET,
    payload: {
        targets,
    },
});

export const ModifyCampaignMap = (payload: Map<string, any>): ModifyCampaignMapAction => ({
    type: CampaignCreatorActionTypes.MODIFY_CAMPAIGN_MAP,
    payload,
});

export const LoadCampaignStart = (name: string): LoadCampaignStartAction => ({
    type: CampaignCreatorActionTypes.LOAD_START,
    payload: {
        name,
    },
});

export const LoadCampaignSuccess = (campaign: Campaign): LoadCampaignSuccessAction => ({
    type: CampaignCreatorActionTypes.LOAD_SUCCESS,
    payload: campaign,
});

export const LoadCampaignError = (): LoadCampaignErrorAction => ({
    type: CampaignCreatorActionTypes.LOAD_ERROR,
});

export const SaveCampaignStart = ({
    isDraft,
    openDomainValidationScreen,
    redirectURL,
}: SaveCampaignParams): SaveCampaignStartAction => ({
    type: CampaignCreatorActionTypes.SAVE_START,
    payload: {
        isDraft,
        openDomainValidationScreen,
        redirectURL,
    },
    meta: {
        thunk: true,
    },
});

export const SaveCampaignSuccess = ({ meta }: { meta?: Record<string, unknown> }): SaveCampaignSuccessAction => ({
    type: CampaignCreatorActionTypes.SAVE_SUCCESS,
    meta,
});

export const SaveCampaignError = (): SaveCampaignErrorAction => ({
    type: CampaignCreatorActionTypes.SAVE_ERROR,
});

export const ClearPickedCampaign = (): ClearPickedCampaignAction => ({
    type: CampaignCreatorActionTypes.CLEAR_PICKED_CAMAPAIGN,
});

export const VerifyCampaign = (): VerifyCampaignAction => ({
    type: CampaignCreatorActionTypes.VERIFY_CAMPAIGN,
});

export const CountRecipientsStart = (isAllContactsSelected: boolean): CountRecipientsStartAction => ({
    type: CampaignCreatorActionTypes.COUNT_RECIPIENTS_START,
    payload: {
        isAllContactsSelected,
    },
});

export const CountRecipientsSuccess = (count: number): CountRecipientsSuccessAction => ({
    type: CampaignCreatorActionTypes.COUNT_RECIPIENTS_SUCCESS,
    payload: count,
});

export const CountRecipientsError = (): CountRecipientsErrorAction => ({
    type: CampaignCreatorActionTypes.COUNT_RECIPIENTS_ERROR,
});

export const SetCampaignErrors = (
    errors: CampaignErrors,
    activeTabsIndexes?: {
        activeTemplateIndex?: number;
        activeUtmTabIndex?: number;
    }
): SetCampaignErrorsAction => ({
    type: CampaignCreatorActionTypes.SET_CAMPAIGN_ERRORS,
    payload: {
        errors,
        activeTabsIndexes,
    },
});

export const SetCampaignErrorsSubscriber = (
    errorsSubscriber: CampaignErrorSubscriber
): SetCampaignErrorsSubscriberAction => ({
    type: CampaignCreatorActionTypes.SET_CAMPAIGN_ERRORS_SUBSCRIBER,
    payload: errorsSubscriber,
});

export const SetFirstErrorFocused = (): SetFirstErrorFocusedAction => ({
    type: CampaignCreatorActionTypes.SET_FRIST_ERROR_FOCUSED,
});

export const SelectTemplate = (
    templateName: string,
    isPublic?: boolean,
    isFromGallery?: boolean
): SelectTemplateAction => ({
    type: CampaignCreatorActionTypes.SELECT_TEMPLATE,
    payload: {
        templateName,
        isPublic,
        isFromGallery,
    },
});

export const DeselectTemplate = (index: number): DeselectTemplateAction => ({
    type: CampaignCreatorActionTypes.DESELECT_TEMPLATE,
    payload: {
        index,
    },
});

export const OpenGallery = (): OpenGalleryAction => ({
    type: CampaignCreatorActionTypes.OPEN_GALLERY,
});

export const CloseGallery = (): CloseGalleryAction => ({
    type: CampaignCreatorActionTypes.CLOSE_GALLERY,
});

export const OpenEditorDragAndDrop = (
    templateName?: string,
    isPublic?: boolean,
    isEditedFromGallery?: boolean
): OpenEditorDragAndDropAction => ({
    type: CampaignCreatorActionTypes.OPEN_EDITOR_DRAG_AND_DROP,
    payload: {
        templateName,
        isPublic,
        isEditedFromGallery,
    },
});

export const CloseEditorDragAndDrop = (): CloseEditorDragAndDropAction => ({
    type: CampaignCreatorActionTypes.CLOSE_EDITOR_DRAG_AND_DROP,
});

export const OpenTemplateEditor = (templateName?: string): OpenTemplateEditorAction => ({
    type: CampaignCreatorActionTypes.OPEN_TEMPLATE_EDITOR,
    payload: {
        templateName,
    },
});

export const CloseTemplateEditor = (): CloseTemplateEditorAction => ({
    type: CampaignCreatorActionTypes.CLOSE_TEMPLATE_EDITOR,
});

export const LoadTemplateStart = (templateName: string, index: number): LoadTemplateStartAction => ({
    type: CampaignCreatorActionTypes.LOAD_TEMPLATE_START,
    payload: {
        templateName,
        index,
    },
});

export const LoadTemplateSuccess = (
    template: Template,
    index: number,
    isFromGallery?: boolean
): LoadTemplateSuccessAction => ({
    type: CampaignCreatorActionTypes.LOAD_TEMPLATE_SUCCESS,
    payload: {
        template,
        index,
        isFromGallery,
    },
});

export const LoadTemplateError = (index: number): LoadTemplateErrorAction => ({
    type: CampaignCreatorActionTypes.LOAD_TEMPLATE_ERROR,
    payload: {
        index,
    },
});

export const AddCampaignAttachment = (file: File, templateIndex: number): AddCampaignAttachmentAction => ({
    type: CampaignCreatorActionTypes.ADD_ATTACHMENT,
    payload: {
        file,
        templateIndex,
    },
});

export const RemoveCampaignAttachment = (fileName: string, templateIndex: number): RemoveCampaignAttachmentAction => ({
    type: CampaignCreatorActionTypes.REMOVE_ATTACHMENT,
    payload: {
        fileName,
        templateIndex,
    },
});

export const CheckEnteredDomainStart = (email: string): CheckEnteredDomainStartAction => ({
    type: CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_START,
    payload: {
        email,
    },
});

export const CheckEnteredDomainSuccess = (isFreeDomain: boolean): CheckEnteredDomainSuccessAction => ({
    type: CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_SUCCESS,
    payload: {
        isFreeDomain,
    },
});

export const CheckEnteredDomainError = (): CheckEnteredDomainErrorAction => ({
    type: CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_ERROR,
});

export const SetActiveTemplateIndex = (index: number): SetActiveTemplateIndexAction => ({
    type: CampaignCreatorActionTypes.SET_ACTIVE_TEMPLATE_INDEX,
    payload: index,
});

export const SetActiveUtmTabIndex = (index: number): SetActiveUtmTabIndexAction => ({
    type: CampaignCreatorActionTypes.SET_ACTIVE_UTM_TAB_INDEX,
    payload: index,
});

export const LoadCompletedCampaignsCountStart = (): LoadCompletedCampaignsCountStartAction => ({
    type: CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_START,
});

export const LoadCompletedCampaignsCountSuccess = (count: number): LoadCompletedCampaignsCountSuccessAction => ({
    type: CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_SUCCESS,
    payload: count,
});

export const SetAbTabs = (abTabs: AbTabsType[]): SetAbTabsAction => ({
    type: CampaignCreatorActionTypes.SET_AB_TABS,
    payload: {
        abTabs,
    },
});

export const SetAbRecipients = (): SetAbRecipientsAction => ({
    type: CampaignCreatorActionTypes.SET_AB_RECIPIENTS,
});

export const SetRecalcAbRecipients = (abRecipients: AbRecipientsType[]): SetRecalcAbRecipientsAction => ({
    type: CampaignCreatorActionTypes.SET_RECALC_AB_RECIPIENTS,
    payload: {
        abRecipients,
    },
});

export const StartRemoveAbTabs = (id: number): StartRemoveAbTabsAction => ({
    type: CampaignCreatorActionTypes.START_REMOVE_AB_TABS,
    payload: {
        id,
    },
});

export const RemoveAbTabs = (id: number): RemoveAbTabsAction => ({
    type: CampaignCreatorActionTypes.REMOVE_AB_TABS,
    payload: {
        id,
    },
});

export const SetAbTabsError = (): SetAbTabsErrorAction => ({
    type: CampaignCreatorActionTypes.SET_AB_TABS_ERROR,
});

export const LoadCompletedCampaignsCountError = (): LoadCompletedCampaignsCountErrorAction => ({
    type: CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_ERROR,
});

export const SendTest = (): SendTestAction => ({
    type: CampaignCreatorActionTypes.SEND_TEST,
});

export const SaveInMyTemplates = (params: SaveInMyTemplatesType): SaveInMyTemplatesAction => ({
    type: CampaignCreatorActionTypes.SAVE_IN_MY_TEMPLATES,
    payload: {
        params,
    },
});

export const ModifyCampaignSendTest = (objectPath: string, value: any): ModifyCampaignSendTestAction => ({
    type: CampaignCreatorActionTypes.MODIFY_CAMPAIGN_SEND_TEST,
    payload: {
        objectPath,
        value,
    },
});

export const ContinueWithHTMLTemplate = (): ContinueWithHTMLTemplateAction => ({
    type: CampaignCreatorActionTypes.CONTINUE_WITH_HTML_TEMPLATE,
});

export const EditCampaignTemplate = (
    templateName?: string,
    templateType?: TemplateType,
    isPublic?: boolean,
    isEditedFromGallery?: boolean
): EditCampaignTemplateAction => ({
    type: CampaignCreatorActionTypes.EDIT_CAMPAIGN_TEMPLATE,
    payload: {
        templateName,
        templateType,
        isPublic,
        isEditedFromGallery,
    },
});

export const OpenPreviewOverlay = (): OpenPreviewOverlayAction => ({
    type: CampaignCreatorActionTypes.OPEN_PREVIEW_OVERLAY,
});

export const ClosePreviewOverlay = (): ClosePreviewOverlayAction => ({
    type: CampaignCreatorActionTypes.CLOSE_PREVIEW_OVERLAY,
});

export const SwitchUTM = (): SwitchUTMAction => ({
    type: CampaignCreatorActionTypes.SWITCH_UTM,
});

export const SetSelectedTemplates = (selectedTemplates: SelectedTemplateType[]): SetSelectedTemplatesAction => ({
    type: CampaignCreatorActionTypes.SET_SELECTED_TEMPLATES,
    payload: {
        selectedTemplates,
    },
});

export const AddNewCampaignTemplate = (
    defaultSenderEmail: string,
    defaultSenderName: string
): AddNewCampaignTemplateAction => ({
    type: CampaignCreatorActionTypes.ADD_NEW_CAMPAIGN_TEMPLATE,
    payload: {
        defaultSenderEmail,
        defaultSenderName,
    },
});

export const LoadCampaignSimpleStatsStart = (name: string, from: string): LoadCampaignSimpleStatsStartAction => ({
    type: CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_START,
    payload: {
        name,
        from,
    },
});

export const LoadCampaignSimpleStatsSuccess = (
    campaignSimpleStats: CampaignSimpleStats
): LoadCampaignSimpleStatsSuccessAction => ({
    type: CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_SUCCESS,
    payload: {
        campaignSimpleStats,
    },
});

export const LoadCampaignSimpleStatsError = (): LoadCampaignSimpleStatsErrorAction => ({
    type: CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_ERROR,
});

export const LoadAttachmentStart = (): LoadAttachmentStartAction => ({
    type: CampaignCreatorActionTypes.LOAD_ATTACHMENT_START,
});

export const LoadAttachmentFinish = (): LoadAttachmentFinishAction => ({
    type: CampaignCreatorActionTypes.LOAD_ATTACHMENT_FINISH,
});

export const ReshardValidationFailed = ({ meta }: { meta: Record<string, unknown> }) => ({
    type: CampaignCreatorActionTypes.RESHARD_VALIDATION_FAILED,
    meta,
    error: true,
});

export const getCampainCreatorStore = (state: ApplicationState): CampaignCreatorState =>
    state?.campaignCreator || ({} as CampaignCreatorState);
export const getGalleryOpenStore = (state: ApplicationState): boolean => state?.campaignCreator?.isGalleryOpen;
